<template>
	<!-- MYS-7340: "Quick Menu (Call to action buttons)", see Figma ADJ-30484 -->
	<div class="CtaButtonsField" :class="{ 'translation-mode': translationMode }" :data-cy="dataCy">
		<div class="cta-grid cta-header">
			<span class="placeholder"></span>
			<span v-if="translationMode"></span>
			<span>{{ $t('text.ctaButtonLabel') }}</span>
			<span>{{ $t('text.ctaActionType') }}</span>
			<span>{{ $t('text.ctaContent') }}</span>
			<span class="placeholder"></span>
		</div>

		<div v-if="!model.length" class="cta-empty">{{ $t('text.ctaNoItemsAdded') }}</div>

		<draggable v-model="model" handle=".handle" item-key="sys.id" :disabled="disabled || translationMode" @end="emitChange">
			<div v-for="(entry, e) of model" :key="entry.sys.id" class="cta-entry">
				<div v-for="(rowLocale, r) of rowLocales" :key="rowLocale" class="cta-grid cta-row">
					<v-icon v-if="r === 0" class="handle" :class="{ inactive: disabled || translationMode }">mdi-drag</v-icon>
					<span v-else></span>

					<!-- wrapped: the global .flag class positions flags absolutely -->
					<span v-if="translationMode" class="cta-flag"><LanguageFlag :modelValue="rowLocale" /></span>

					<v-text-field variant="outlined" density="compact" hide-details="auto"
						:modelValue="entry.fields.label[rowLocale]"
						@update:modelValue="setLabel(entry, rowLocale, $event)"
						:placeholder="labelPlaceholder(entry, rowLocale)"
						:maxlength="maxLabelLength"
						:disabled="disabled"
						:error-messages="showError(entry, 'label', rowLocale) ? [ $t('text.ctaLabelRequired') + defaultLocaleHint(rowLocale) ] : []"
						:data-cy="`cta-label-${e}-${rowLocale}`"
					/>

					<RadioSelect v-if="r === 0" class="cta-type"
						menuClass="cta-type-menu"
						:modelValue="entry.fields.actionType"
						@update:modelValue="setActionType(entry, $event)"
						:options="actionTypeOptions"
						:placeholder="$t('text.ctaChooseActionType')"
						:disabled="disabled || translationMode"
						:errorMessages="showError(entry, 'actionType') ? [ $t('text.ctaActionTypeRequired') ] : []"
						:dataCy="`cta-type-${e}`"
					/>
					<span v-else></span>

					<div class="cta-content">
						<span v-if="!entry.fields.actionType" class="cta-hint">{{ $t('text.ctaChooseActionTypeFirst') }}</span>

						<template v-else-if="entry.fields.actionType === CtaActionType.DOWNLOAD">
							<div v-if="entry.fields.assets[rowLocale]" class="cta-file">
								<div class="cta-file-text">
									<div class="cta-file-name">{{ entry.fields.assets[rowLocale].fileName }}</div>
									<div class="cta-file-meta">PDF{{ entry.fields.assets[rowLocale].size ? ' · ' + formatFileSize(entry.fields.assets[rowLocale].size) : '' }}</div>
								</div>
								<v-icon v-if="!disabled" size="small" @click="removeFile(entry, rowLocale)">mdi-close</v-icon>
							</div>
							<v-btn v-else variant="outlined" class="cta-file-button" :loading="isUploading(entry, rowLocale)" :disabled="disabled"
								:class="{ 'has-error': showError(entry, 'file', rowLocale) }"
								:color="showError(entry, 'file', rowLocale) ? 'error' : undefined"
								@click="chooseFile(entry, rowLocale)"
							>
								<v-icon start>mdi-plus</v-icon>
								<span class="cta-file-button-label" :title="fileButtonLabel(rowLocale)">{{ fileButtonLabel(rowLocale) }}</span>
							</v-btn>
						</template>

						<v-text-field v-else variant="outlined" density="compact" hide-details="auto"
							:modelValue="contentValue(entry, rowLocale)"
							@update:modelValue="setContentValue(entry, rowLocale, $event)"
							:placeholder="contentPlaceholder(entry, rowLocale)"
							:disabled="disabled"
							:error-messages="showError(entry, 'value', rowLocale) ? [ $t(valueErrorKey(entry)) + defaultLocaleHint(rowLocale, entry) ] : []"
							:data-cy="`cta-value-${e}-${rowLocale}`"
						/>
					</div>

					<!-- the translation wizard shows the list structure, but only texts / files can be changed there -->
					<button v-if="r === 0 && !disabled" type="button" class="delete" :disabled="translationMode" @click="remove(e)" :data-cy="`cta-delete-${e}`">
						<v-icon class="delete-icon">mdi-delete</v-icon>
						<v-icon class="delete-icon-hover" size="36">mdi-delete-circle-outline</v-icon>
					</button>
					<span v-else></span>
				</div>
			</div>
		</draggable>

		<div v-if="fileError" class="cta-file-error">{{ fileError }}</div>

		<div v-if="!disabled" class="cta-button-cont">
			<!-- stays visible when the limit is reached, so it is clear that no more buttons can be added -->
			<button class="cta-add" type="button" :disabled="translationMode || model.length >= MAX_CTA_BUTTONS" @click="add" data-cy="cta-add">
				<v-icon>mdi-plus</v-icon>
				{{ $t('text.add') }}
			</button>
		</div>

		<input ref="fileInput" type="file" accept="application/pdf,.pdf" style="display: none" @change="onFileSelected" />
	</div>
</template>

<script lang="ts">
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import LanguageFlag from '../common/LanguageFlag.vue'
import RadioSelect from '../common/RadioSelect.vue'
import { CTA_FILE_MAX_SIZE, CTA_LABEL_MAX_LENGTH } from '../../../../shared/cta-button'
import { CtaActionType, ctaEntryErrors, ctaValueInvalidForLocale, formatFileSize, MAX_CTA_BUTTONS, newCtaEntry } from '../../utils/cta-buttons'
import type { CtaEntry, CtaEntryError } from '../../utils/cta-buttons'
import { clientRequiredLocaleCodes } from '../../utils/translation-policy'

export default {
	name: 'CtaButtonsField',
	components: { draggable, LanguageFlag, RadioSelect },
	props: {
		modelValue: { type: Array as () => CtaEntry[], default: () => [] },
		// set by the translation wizard: one row per locale
		locales: Array as () => string[],
		disabled: Boolean,
		dataCy: String,
	},
	emits: [ 'update:modelValue' ],
	data: () => ({
		model: [] as CtaEntry[],
		CtaActionType,
		MAX_CTA_BUTTONS,
		maxLabelLength: CTA_LABEL_MAX_LENGTH,
		uploading: {} as Record<string, boolean>,
		uploadTarget: null as null | { entry: CtaEntry, locale: string },
		validated: false,
		fileError: '',
	}),
	computed: {
		translationMode(): boolean {
			return Array.isArray(this.locales)
		},
		defaultLocaleCode(): string {
			const { selectedClient, locales, defaultLocale } = this.$store.state
			return clientRequiredLocaleCodes(selectedClient, locales ?? [], defaultLocale)[0] ?? 'de'
		},
		rowLocales(): string[] {
			if (this.translationMode) return this.locales ?? []
			return [ this.$store.state.serviceLocale ?? this.defaultLocaleCode ]
		},
		actionTypeOptions() {
			return [
				{ value: CtaActionType.EXTERNAL_LINK, title: this.$t('text.ctaActionExternalLink') },
				{ value: CtaActionType.PHONE, title: this.$t('text.ctaActionPhone') },
				{ value: CtaActionType.EMAIL, title: this.$t('text.ctaActionEmail') },
				{ value: CtaActionType.DOWNLOAD, title: this.$t('text.ctaActionDownload') },
			]
		},
	},
	watch: {
		modelValue: {
			handler(n) {
				if (n !== this.model) this.model = n ?? []
			},
			immediate: true,
		},
	},
	methods: {
		formatFileSize,
		fileButtonLabel(locale: string): string {
			return locale === this.defaultLocaleCode ? this.$t('text.ctaChooseFile') : this.$t('text.ctaChooseTranslatedFile')
		},
		emitChange() {
			this.$emit('update:modelValue', this.model)
		},
		add() {
			if (this.model.length >= MAX_CTA_BUTTONS) return
			this.model.push(newCtaEntry())
			this.emitChange()
		},
		remove(index: number) {
			this.model.splice(index, 1)
			this.emitChange()
		},
		setLabel(entry: CtaEntry, locale: string, value: string) {
			entry.fields.label = { ...entry.fields.label, [locale]: value }
			this.emitChange()
		},
		setActionType(entry: CtaEntry, actionType: CtaActionType) {
			if (entry.fields.actionType === actionType) return
			entry.fields.actionType = actionType
			// the content of another type does not make sense any more
			entry.fields.value = {}
			entry.fields.assets = {}
			this.emitChange()
		},
		contentValue(entry: CtaEntry, locale: string): string {
			return entry.fields.value?.[locale] ?? ''
		},
		setContentValue(entry: CtaEntry, locale: string, value: string) {
			entry.fields.value = { ...entry.fields.value, [locale]: value }
			this.emitChange()
		},
		labelPlaceholder(entry: CtaEntry, locale: string): string {
			if (locale !== this.defaultLocaleCode && entry.fields.label?.[this.defaultLocaleCode]) return entry.fields.label[this.defaultLocaleCode]
			return this.$t('text.ctaEnterButtonLabel')
		},
		contentPlaceholder(entry: CtaEntry, locale: string): string {
			// other languages are not prefilled: the default value is only a hint, it is used when the field stays empty
			if (locale !== this.defaultLocaleCode && entry.fields.value?.[this.defaultLocaleCode]) return entry.fields.value[this.defaultLocaleCode]
			if (entry.fields.actionType === CtaActionType.EXTERNAL_LINK) return 'https://'
			if (entry.fields.actionType === CtaActionType.PHONE) return '+43 123 456789'
			if (entry.fields.actionType === CtaActionType.EMAIL) return 'info@example.com'
			return ''
		},
		valueErrorKey(entry: CtaEntry): string {
			if (entry.fields.actionType === CtaActionType.PHONE) return 'text.ctaInvalidPhone'
			if (entry.fields.actionType === CtaActionType.EMAIL) return 'text.ctaInvalidEmail'
			return 'text.ctaInvalidUrl'
		},
		isMissingInDefaultLocale(entry: CtaEntry, error: CtaEntryError): boolean {
			return error === 'value'
				? !entry.fields.value?.[this.defaultLocaleCode]?.trim() && ctaEntryErrors(entry, this.defaultLocaleCode).includes('value')
				: ctaEntryErrors(entry, this.defaultLocaleCode).includes(error)
		},
		showError(entry: CtaEntry, error: CtaEntryError, locale?: string): boolean {
			if (!this.validated) return false
			// format errors belong to the row that holds the value
			if (locale && error === 'value' && ctaValueInvalidForLocale(entry, locale, this.defaultLocaleCode)) return true
			if (!locale || locale === this.defaultLocaleCode) return ctaEntryErrors(entry, this.defaultLocaleCode).includes(error)
			// required errors belong to the default locale: the wizard shows that row itself, outside the wizard
			// only the selected language is visible, so the error goes there (see defaultLocaleHint)
			return !this.translationMode && this.isMissingInDefaultLocale(entry, error)
		},
		/** outside the wizard the visible row can be another language than the required default one: say which */
		defaultLocaleHint(locale: string, entry?: CtaEntry): string {
			if (this.translationMode || locale === this.defaultLocaleCode) return ''
			if (entry && !this.isMissingInDefaultLocale(entry, 'value')) return ''
			const name = this.$store.state.locales?.find(l => l.code === this.defaultLocaleCode)?.name ?? this.defaultLocaleCode.toUpperCase()
			return ` (${name})`
		},
		/** @returns true when all buttons are valid; shows the errors from now on */
		validate(): boolean {
			this.validated = true
			return this.model.every(entry => ctaEntryErrors(entry, this.defaultLocaleCode).length === 0)
		},
		uploadKey(entry: CtaEntry, locale: string): string {
			return entry.sys.id + '-' + locale
		},
		isUploading(entry: CtaEntry, locale: string): boolean {
			return !!this.uploading[this.uploadKey(entry, locale)]
		},
		chooseFile(entry: CtaEntry, locale: string) {
			this.uploadTarget = { entry, locale }
			this.fileError = ''
			const fileInput = this.$refs.fileInput as HTMLInputElement
			fileInput.value = ''
			fileInput.click()
		},
		async onFileSelected(event) {
			const file: File = event.target.files?.[0]
			const target = this.uploadTarget
			this.uploadTarget = null
			if (!file || !target) return

			const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')
			if (!isPdf || file.size > CTA_FILE_MAX_SIZE) {
				this.fileError = this.$t('text.ctaOnlyPdf')
				return
			}

			const key = this.uploadKey(target.entry, target.locale)
			this.uploading[key] = true
			try {
				const serviceProviderId = this.$store.state.selectedServiceProvider?.sys?.id
				const query = serviceProviderId ? `?serviceProviderId=${encodeURIComponent(serviceProviderId)}` : ''
				const result = await this.$httpUpload(`/upload${query}`, file)
				if (!result?.assetId) throw new Error('no asset id returned')
				target.entry.fields.assets = {
					...target.entry.fields.assets,
					[target.locale]: { id: result.assetId, fileName: file.name, size: file.size },
				}
				this.emitChange()
			}
			catch (error) {
				console.error('CtaButtonsField upload failed', error)
				this.fileError = this.$t('text.ctaUploadFailed')
			}
			finally {
				this.uploading[key] = false
			}
		},
		removeFile(entry: CtaEntry, locale: string) {
			const assets = { ...entry.fields.assets }
			delete assets[locale]
			entry.fields.assets = assets
			this.emitChange()
		},
	},
}
</script>

<style scoped lang="scss">
// App.vue adds `div.field .v-input { margin-top: 12px }`, which offsets the inputs of the grid unevenly
.cta-row :deep(.v-input) { margin-top: 0; }
// RadioSelect always renders its (here unused) help text paragraph
.cta-type :deep(p:empty) { display: none; }
// locked action type (translation wizard): grey fill instead of Vuetify's faded disabled look
.cta-type :deep(.v-field--disabled) {
	opacity: 1;
	background: #F4F4F4;
	color: #8F8F8F;

	.v-field__outline { --v-field-border-opacity: 1; color: #DBDBDB; }
	.v-select__selection-text,
	.v-field__append-inner .v-icon { color: #8F8F8F; opacity: 1; }
}

$line: #e0e0e0;
$muted: #9e9e9e;
$secondary: #757575;
$control-height: 40px;
$row-height: 60px;
$row-padding: ($row-height - $control-height) * 0.5; // centres a control in an error-free row
$icon-offset: ($control-height - 24px) * 0.5; // centres a 24px icon on a control

@mixin inactive {
	cursor: default;
	opacity: 0.4;
}

.CtaButtonsField {
	margin-top: 16px;

	&.translation-mode .cta-grid {
		grid-template-columns: 48px 24px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 48px;
	}
}

.cta-grid {
	display: grid;
	grid-template-columns: 48px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 48px;
	align-items: start;
}

.cta-header {
	font-size: 14px;
	font-weight: 500;
	border-bottom: 1px solid $line;
	> span {
		padding: 14px 12px;
		color: #000;
		font-size: 15px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: -0.037px;
	}
}

.cta-entry {
	border-bottom: 1px solid $line;
}

.cta-row {
	// top aligned, not centred: a validation message below a field grows the row
	// instead of overlapping the next one, and all controls stay on one line
	min-height: $row-height;
	padding: $row-padding 0;

	> i,
	> div {
		padding: 0 12px;
	}
}

.handle {
	cursor: grab;
	margin: $icon-offset auto 0;
	color: $muted;
	&.inactive { @include inactive; }
}

$delete-size: 36px;

.delete {
	display: flex;
	align-items: center;
	justify-content: center;
	width: $delete-size;
	height: $delete-size;
	margin: ($control-height - $delete-size) * 0.5 auto 0;
	cursor: pointer;
	color: #000;

	.delete-icon-hover { display: none; }

	&:hover:not(:disabled),
	&:focus-visible {
		color: var(--color-blue, rgb(0, 174, 239));
		.delete-icon { display: none; }
		.delete-icon-hover { display: inline-flex; }
	}

	&:disabled { @include inactive; }
}

.cta-flag {
	display: flex;
	align-items: center;
	height: $control-height;

	img {
		width: 20px;
		height: 20px;
	}
}

.cta-empty {
	display: flex;
	align-items: center; // vertical centre, needs the flex container
	height: 60px;
	padding-left: 60px;
	color: $muted;
	border-bottom: 1px solid $line;
	font-size: 15px;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: -0.037px;
}

.cta-hint {
	display: flex;
	align-items: center;
	min-height: $control-height;
	color: #8F8F8F;
	text-overflow: ellipsis;
	font-size: 15px;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: -0.037px;
}

.cta-file {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: $control-height;
	padding: 2px 16px; // name + meta fit the height of the other controls
	border-radius: 16px;
	background: rgba(244, 244, 244, 0.50);

	&-text { 
		overflow: hidden;
		color: var(--primary-color-dark, #000);
	 }

	&-name {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		font-size: 15px;
		font-weight: 400;
		line-height: 20px;
		letter-spacing: -0.037px;
	}

	&-meta {
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.001px;
	}

	&-button {
		width: 100%;
		text-transform: none;
		letter-spacing: normal;
		border-radius: 8px;
		border: 1px solid #424242;
		height: 40px;

		// the fixed border above would otherwise override the red of color="error"
		&.has-error { border-color: rgb(var(--v-theme-error)); }

		// v-btn never wraps and centres its content: long labels ("Übersetzte Datei auswählen" in the
		// narrow wizard column) would overflow on both sides, so let the label shrink and truncate
		min-width: 0;
		padding: 0 12px;
		:deep(.v-btn__content) {
			min-width: 0;
			max-width: 100%;
		}
		&-label {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&-error {
		color: var(--col-error, #da294a);
		font-size: 12px;
		font-weight: 600;
		margin-top: 8px;
	}
}

.cta-button-cont {
	padding: 12px;	
	display: flex;
	.cta-add {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		width: 100%;
		border: 1px dashed #424242;
		border-radius: 4px;
		color: #424242;
		height: 40px;

		font-size: 15px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: -0.037px;
		
		&:hover:not(:disabled) { background: #fafafa; }
		&:disabled {
			cursor: default;
			border-color: #DBDBDB;
			color: #8F8F8F;
		}
	}
}
</style>

<style lang="scss">
// the action type menu is teleported to <body>: unscoped, but limited to its own class
.cta-type-menu {
	.v-list {
		padding: 0;
	}

	.v-list-item {
		min-height: 60px;
	}
}
</style>
